import { DEFAULT_HEALTH, NEEDS, formatPence } from '@london-life/contracts';
import { api } from '../lib/api';
import { useCatalogue } from '../lib/content';
import { actionKey, londonWhen } from '../lib/format';
import { PhoneAppFrame, usePhoneApp, type PhoneAppProps } from '../lib/phone-app';

// Health, as Lagos Life's 💊 Health: how you are (healthy, or ill and how badly), a warning when needs are in the
// red, medicine from the pharmacy (delivered, mild only), the GP at the clinic (treats anything), and what
// happens if it is left
export function HealthScreen(props: PhoneAppProps) {
  const content = useCatalogue()?.health ?? DEFAULT_HEALTH;
  const { reply, failure, busy, load, run } = usePhoneApp(() => api.health.mine(), props);
  const health = reply?.health;
  const sick = health?.sick ?? null;
  const wallet = reply?.walletPence ?? 0;
  return (
    <PhoneAppFrame
      id="health"
      title="💊 Health"
      className="health-screen"
      closeLabel="Close Health"
      onClose={props.onClose}
      failure={failure}
      onRetry={() => void load()}
      loading={reply ? undefined : 'Checking how you are…'}
    >
      {health && (
        <div className="business-detail">
          <section
            className={`health-status ${sick ? sick.level : 'well'}`}
            aria-label={sick ? `${sick.name}, ${sick.level}` : "You're healthy"}
          >
            <span aria-hidden="true">{sick ? sick.emoji : '💪🏾'}</span>
            <strong>{sick ? sick.name : "You're healthy"}</strong>
            <small>
              {sick
                ? sick.level === 'serious'
                  ? "Serious · you can't work until a doctor treats you"
                  : 'Mild · treat it before it gets worse'
                : health.atRisk
                  ? "Careful: several needs are in the red. Leave them too long and you'll fall ill."
                  : "Keep eating, sleeping and washing and you'll stay that way."}
            </small>
            {sick && <p>{sick.text}</p>}
            {sick && (
              <small>
                {sick.level === 'serious' ? 'Hospital' : 'Serious'} if left until {londonWhen(sick.worseAt)}
              </small>
            )}
          </section>
          {health.redNeeds.length > 0 && (
            <p className="business-warning">
              In the red: {health.redNeeds.map((key) => `${NEEDS[key].icon} ${NEEDS[key].label}`).join(', ')}
            </p>
          )}
          <button
            type="button"
            className="health-option"
            disabled={busy || !sick || sick.level === 'serious' || wallet < health.pharmacyPence}
            onClick={() => void run(() => api.health.pharmacy(actionKey('pharmacy')))}
          >
            <span aria-hidden="true">💊</span>
            <span>
              <strong>Order medicine from the pharmacy</strong>
              <small>For mild illness · delivered to you · {formatPence(health.pharmacyPence)}</small>
            </span>
          </button>
          <button
            type="button"
            className="health-option"
            disabled={busy}
            onClick={() =>
              health.clinic.here ? props.onDo?.(content.doctorActivityId) : props.onPlace?.(health.clinic.placeId)
            }
          >
            <span aria-hidden="true">{health.clinic.here ? '🩺' : '🏥'}</span>
            <span>
              <strong>{health.clinic.here ? 'See the GP now' : `Go to ${health.clinic.name}`}</strong>
              <small>
                Treats anything · free on the NHS
                {health.prescriptionPence ? `, prescription ${formatPence(health.prescriptionPence)}` : ''}
              </small>
            </span>
          </button>
          <p className="business-muted">
            Left untreated, mild illness turns serious after {content.seriousAfterHours} hours, and serious illness puts
            you in hospital after {content.hospitalAfterHours} more.
          </p>
        </div>
      )}
    </PhoneAppFrame>
  );
}
